// components/NotificationCenter.js
import React, { useState, useEffect, useRef } from "react";
import WebSocketService from "../services/websocketService";
import NotificationApiService from "../services/notificationApiService";
import SoundService from "../services/soundService";
import "./NotificationCenter.css";
import { Badge, Box, Button, Chip, Paper, Typography } from "@mui/material";
import {
  Cancel,
  CancelOutlined,
  Check,
  CheckCircleOutline,
  ErrorOutline,
  Face,
  ForkRight,
  Help,
  Mail,
  NotificationAdd,
  ReportGmailerrorred,
  Warning,
} from "@mui/icons-material";

const NotificationCenter = ({ loggerUser }) => {
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [volume, setVolume] = useState(0.7);
  const [userInteracted, setUserInteracted] = useState(false);
  const pendingNotifications = useRef([]);

  // Sound configuration based on notification type
  const soundConfig = {
    DATABASE_UPDATE: "notification",
    SUCCESS: "success",
    ERROR: "error",
    ALERT: "alert",
    default: "notification",
  };

  useEffect(() => {
    // Initialize sound service
    if (SoundService.isSupported()) {
      SoundService.preloadSounds();
      SoundService.setVolume(volume);
      SoundService.initAudioContext(); // Initialize audio context
    }

    // Add user interaction listeners
    const handleUserInteraction = async () => {
      if (!userInteracted) {
        setUserInteracted(true);

        // Resume audio context on first user interaction
        await SoundService.resumeAudioContext();

        // Process any pending notifications
        processPendingNotifications();

        // Remove event listeners after first interaction
        document.removeEventListener("click", handleUserInteraction);
        document.removeEventListener("keydown", handleUserInteraction);
        document.removeEventListener("touchstart", handleUserInteraction);
      }
    };

    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    // Load initial notifications
    loadNotifications();

    // Connect to WebSocket
    WebSocketService.connect(
      () => {
        setIsConnected(true);
        setupWebSocketSubscriptions();
      },
      (error) => {
        setIsConnected(false);
        console.error("WebSocket connection failed:", error);
      }
    );

    // Request browser notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    // Cleanup on unmount
    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
      WebSocketService.disconnect();
    };
  }, []);

  useEffect(() => {
    SoundService.setVolume(volume);
  }, [volume]);

  const processPendingNotifications = () => {
    if (pendingNotifications.current.length > 0) {
      pendingNotifications.current.forEach((notification) => {
        playNotificationSound(notification);
      });
      pendingNotifications.current = [];
    }
  };

  const playNotificationSound = async (notification) => {
    if (!soundEnabled) return;

    if (userInteracted) {
      const soundType = soundConfig[notification.type] || soundConfig.default;
      await SoundService.playSound(soundType);
    } else {
      // Store notification for later when user interacts
      pendingNotifications.current.push(notification);
    }
  };

  // Add a debug function to check sound status
  const debugSoundStatus = () => {
    console.log("Sound loading status:", SoundService.getLoadingStatus());
    console.log("User interacted:", userInteracted);
    console.log("Sound enabled:", soundEnabled);
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await NotificationApiService.getAllNotifications(true);

      // Filter notifications for the logged-in user (user ID 1)
      setNotifications(
        data.filter((notif) => notif.assignUser?.id === loggerUser.id)
      );
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const setupWebSocketSubscriptions = () => {
    // Subscribe to new notifications - FILTER for user ID 1
    WebSocketService.subscribeToNotifications((action, notification) => {
      if (
        action === "NEW_NOTIFICATION" &&
        notification.assignUser?.id === loggerUser.id
      ) {
        setNotifications((prev) => [notification, ...prev]);

        // Play sound effect
        playNotificationSound(notification);

        // Show browser notification
        if (Notification.permission === "granted") {
          new Notification("New Notification", {
            body: notification.message,
            icon: "/notification-icon.png",
            tag: notification.id, // Prevent duplicate notifications
          });
        }
      }
    });

    // Subscribe to individual notification updates - FILTER for user ID 1
    WebSocketService.subscribeToNotificationUpdates(
      (action, updatedNotification) => {
        if (
          action === "UPDATE_NOTIFICATION" &&
          updatedNotification.assignUser?.id === loggerUser.id
        ) {
          setNotifications((prev) =>
            prev.map((notif) =>
              notif.id === updatedNotification.id ? updatedNotification : notif
            )
          );
        }
      }
    );

    // Subscribe to bulk updates - FILTER for user ID 1
    WebSocketService.subscribeToBulkUpdates((action, notificationIds) => {
      if (action === "BULK_UPDATE") {
        setNotifications((prev) =>
          prev.map((notif) =>
            notificationIds.includes(notif.id) &&
            notif.assignUser?.id === loggerUser.id
              ? { ...notif, isRead: true }
              : notif
          )
        );
      }
    });
  };

  const toggleSound = () => {
    const newSoundState = !soundEnabled;
    setSoundEnabled(newSoundState);

    if (newSoundState && userInteracted) {
      // Play a test sound when enabling (only after interaction)
      SoundService.playSound("success");
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
  };

  const markAsRead = async (id) => {
    try {
      setNotifications((prev) => prev.filter((notif) => notif.id !== id));

      await NotificationApiService.markAsRead(id);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === id ? { ...notif, isRead: false } : notif
        )
      );
      alert("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, isRead: true }))
      );

      await NotificationApiService.markAllAsRead();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      loadNotifications();
      alert("Failed to mark all notifications as read");
    }
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  const testSound = () => {
    if (userInteracted) {
      SoundService.playSound("success");
    } else {
      // Show message to user
      alert(
        "Please interact with the page first (click anywhere) to enable sounds"
      );
    }
  };

  // Calculate unread count only for the logged-in user
  const unreadCount = notifications.filter((notif) => !notif.isRead).length;

  console.log("unread count ", unreadCount, notifications);

  return (
    <div>
      {unreadCount === 0 ? (
        ""
      ) : (
        <div className="notification-center">
          <div className="notification-header">
            <div className="notification-title">
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <span className="unread-badge">{unreadCount}</span>
              )}
            </div>
            <div className="header-actions">
              {/* Sound Controls */}
              <div className="sound-controls">
                <button
                  onClick={toggleSound}
                  className={`sound-toggle ${
                    soundEnabled ? "enabled" : "disabled"
                  }`}
                  title={soundEnabled ? "Mute sounds" : "Enable sounds"}
                >
                  {soundEnabled ? "🔊" : "🔇"}
                </button>

                {soundEnabled && (
                  <div className="volume-control">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={(e) =>
                        handleVolumeChange(parseFloat(e.target.value))
                      }
                      className="volume-slider"
                    />
                  </div>
                )}

                <button
                  onClick={testSound}
                  className="test-sound-btn"
                  title="Test sound"
                >
                  🔔
                </button>
              </div>

              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="mark-all-read-btn"
                  disabled={loading}
                >
                  Mark All Read
                </button>
              )}
              <span
                className={`connection-status ${
                  isConnected ? "connected" : "disconnected"
                }`}
              >
                {isConnected ? "● Connected" : "● Disconnected"}
              </span>
            </div>
          </div>

          <div className="notifications-list">
            {loading ? (
              <div className="loading">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <p className="no-notifications">No new notifications</p>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${
                    notification.isRead ? "read" : "unread"
                  }`}
                >
                  <div className="notification-content">
                    <Typography variant="h5" color="info">
                      {notification.message}
                    </Typography>
                    <Typography variant="h6" sx={{ color: "gray" }}>
                      {new Date(
                        notification.entryDateTimeStamp
                      ).toLocaleString()}
                    </Typography>
                    <Box display="flex" justifyContent="space-between">
                      <Chip
                        variant="filled"
                        color={
                          notification.messageStatus
                            ? notification.messageStatus
                            : "info"
                        }
                        icon={
                          notification.messageStatus !== null ? (
                            notification.messageStatus === "error" ? (
                              <CancelOutlined />
                            ) : notification.messageStatus === "warning" ? (
                              <ReportGmailerrorred />
                            ) : notification.messageStatus === "success" ? (
                              <CheckCircleOutline />
                            ) : (
                              <Help />
                            )
                          ) : (
                            <Help />
                          )
                        }
                        label={notification.notificationType}
                      />
                    </Box>
                  </div>
                  <div className="notification-actions">
                    {!notification.isRead && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="mark-read-btn"
                        disabled={loading}
                      >
                        Mark Read
                      </button>
                    )}
                    <button
                      onClick={() => removeNotification(notification.id)}
                      className="remove-btn"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
